import { computed, type ComputedRef } from 'vue';
import type { FormattedString } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { formatString, toFormattedString } from '@/common/FormattedValueHelper';

type UsePersonalDataAccessReturn = {
    /** False when the active role may not identify any employee. */
    canViewPersonalData: ComputedRef<boolean>;
    /** Whether an employee may be selected or opened in a sidebar or slideout. */
    canOpenEmployee: (employment: { isWithinUserOrganization?: boolean }) => boolean;
    /** Raw strings from endpoints that don't send FormattedString yet. */
    personalValue: (value: string | null | undefined) => FormattedString;
    /** Position may replace a hidden name, except for a role that may not see identities. */
    employeeLabel: (name: FormattedString, position: string | null | undefined) => string;
};

/**
 * Personal data access for the active role. Built on the login store, not useUser(),
 * so it also works outside components.
 */
export function usePersonalDataAccess(): UsePersonalDataAccessReturn {
    const loginStore = useLoginStore();

    // Missing means on, like the database default.
    const showPersonalDataForReadOnly = computed<boolean>(() => loginStore.selectedInstance?.settings?.ShowPersonalDataForReadOnly !== false);

    const canViewPersonalData = computed<boolean>(() => loginStore.role?.role !== Role.ReadOnly || showPersonalDataForReadOnly.value);

    function canOpenEmployee(employment: { isWithinUserOrganization?: boolean }): boolean {
        return canViewPersonalData.value && employment.isWithinUserOrganization !== false;
    }

    function personalValue(value: string | null | undefined): FormattedString {
        return toFormattedString(value, canViewPersonalData.value);
    }

    function employeeLabel(name: FormattedString, position: string | null | undefined): string {
        if (name.type === 'hidden' && canViewPersonalData.value && position != null) return position;
        return formatString(name);
    }

    return { canViewPersonalData, canOpenEmployee, personalValue, employeeLabel };
}
